/**
 * Time formatting utilities
 */

export const BUSINESS_TIME_ZONE = 'America/Chicago';

export type TimestampInput = string | Date | null | undefined;

const POSTGRES_TIMESTAMP_WITHOUT_ZONE =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?$/;

const BUSINESS_DATE_KEY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: BUSINESS_TIME_ZONE,
});
const MESSAGE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: BUSINESS_TIME_ZONE,
});
const MESSAGE_DATE_WITH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: BUSINESS_TIME_ZONE,
});
const RELATIVE_FALLBACK_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});
const BUSINESS_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: BUSINESS_TIME_ZONE,
});
const BUSINESS_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: BUSINESS_TIME_ZONE,
});

/** Parse API timestamps without ever leaking an Invalid Date into the UI. */
export function parseTimestamp(value: TimestampInput): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string' || !value.trim()) return null;

  const raw = value.trim();
  const normalized = POSTGRES_TIMESTAMP_WITHOUT_ZONE.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function businessDateKey(value: TimestampInput): string {
  const date = parseTimestamp(value);
  if (!date) return '';
  const parts = BUSINESS_DATE_KEY_FORMATTER.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function isSameMessageDay(left: TimestampInput, right: TimestampInput): boolean {
  const leftKey = businessDateKey(left);
  return !!leftKey && leftKey === businessDateKey(right);
}

/** "Today", "Yesterday", or a compact calendar date in Joseph's business timezone. */
export function formatMessageDateDivider(
  value: TimestampInput,
  now: TimestampInput = new Date(),
): string {
  const date = parseTimestamp(value);
  const nowDate = parseTimestamp(now);
  const dateKey = businessDateKey(date);
  const nowKey = businessDateKey(nowDate);
  if (!date || !nowDate || !dateKey || !nowKey) return '';

  const toOrdinal = (key: string) => {
    const [year, month, day] = key.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  const daysAgo = Math.round((toOrdinal(nowKey) - toOrdinal(dateKey)) / 86_400_000);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';

  return dateKey.slice(0, 4) !== nowKey.slice(0, 4)
    ? MESSAGE_DATE_WITH_YEAR_FORMATTER.format(date)
    : MESSAGE_DATE_FORMATTER.format(date);
}

export function timeAgo(dateStr: string): string {
  const date = parseTimestamp(dateStr);
  if (!date) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return RELATIVE_FALLBACK_DATE_FORMATTER.format(date);
}

export function formatTime(value: TimestampInput): string {
  const d = parseTimestamp(value);
  if (!d) return '';
  return BUSINESS_TIME_FORMATTER.format(d);
}

export function formatDate(value: TimestampInput): string {
  const date = parseTimestamp(value);
  if (!date) return '';
  return BUSINESS_DATE_FORMATTER.format(date);
}

export function formatDateTime(value: TimestampInput): string {
  const date = formatDate(value);
  const time = formatTime(value);
  return date && time ? `${date}, ${time}` : '';
}

export function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  target.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / 86400000);
}

export function formatClosingCountdown(daysOut: number | null): string {
  if (daysOut === null) return '';
  if (daysOut === 0) return 'Closing TODAY';
  if (daysOut === 1) return 'Closing TOMORROW';
  if (daysOut < 0) return `${Math.abs(daysOut)}d overdue`;
  return `${daysOut}d to close`;
}
